// src/pages/marketplace/MarketplaceHomePage.tsx
// Main marketplace landing page with hero, categories, featured and new products.

import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { observer } from '@react';
import { useProduct, useI18n } from '@react/hooks';
import { productApi } from '@core/api';
import {
  Button,
  Input,
  Card,
  CardContent,
  Badge,
  ErrorAlert,
} from '@react/components';

/** Render a row of filled/empty stars for a given rating (0-5). */
function StarRating({ rating, size = 'sm' }: { rating: number; size?: 'sm' | 'md' | 'lg' }) {
  const sizeClass = size === 'lg' ? 'w-6 h-6' : size === 'md' ? 'w-5 h-5' : 'w-4 h-4';
  const full = Math.round(rating);
  return (
    <span className="inline-flex items-center gap-0.5" aria-label={`${rating.toFixed(1)} out of 5 stars`}>
      {Array.from({ length: 5 }, (_, i) => (
        <svg
          key={i}
          className={`${sizeClass} ${i < full ? 'text-yellow-400' : 'text-gray-300'}`}
          fill="currentColor"
          viewBox="0 0 20 20"
        >
          <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.286 3.957a1 1 0 00.95.69h4.162c.969 0 1.371 1.24.588 1.81l-3.37 2.448a1 1 0 00-.364 1.118l1.287 3.957c.3.921-.755 1.688-1.54 1.118l-3.37-2.448a1 1 0 00-1.176 0l-3.37 2.448c-.784.57-1.838-.197-1.539-1.118l1.287-3.957a1 1 0 00-.364-1.118L2.063 9.384c-.783-.57-.38-1.81.588-1.81h4.162a1 1 0 00.95-.69l1.286-3.957z" />
        </svg>
      ))}
    </span>
  );
}

/** Colored placeholder when product has no icon. */
function ProductIcon({ product }: { product: any }) {
  if (product.icon_url) {
    return (
      <img
        src={product.icon_url}
        alt={product.name}
        className="w-12 h-12 rounded-lg object-cover"
      />
    );
  }
  const bg = product.color_primary || '#6366f1';
  const initial = (product.name ?? '?')[0]?.toUpperCase() ?? '?';
  return (
    <div
      className="w-12 h-12 rounded-lg flex items-center justify-center text-white font-bold text-lg"
      style={{ backgroundColor: bg }}
    >
      {initial}
    </div>
  );
}

/** Reusable product card used in grids. */
function ProductCard({ product, marketplace }: { product: any; marketplace?: any }) {
  const rating = marketplace?.average_rating ?? 0;
  return (
    <Link to={`/marketplace/${product.slug}`} className="block group">
      <Card className="h-full transition-all group-hover:shadow-md group-hover:border-primary/20">
        <CardContent className="p-5">
          <div className="flex items-start gap-3.5">
            <ProductIcon product={product} />
            <div className="flex-1 min-w-0">
              <h3 className="text-sm font-semibold text-foreground truncate group-hover:text-primary transition-colors">
                {product.name}
              </h3>
              <p className="text-xs text-muted-foreground mt-1 line-clamp-2 leading-relaxed">
                {product.tagline || product.description}
              </p>
            </div>
          </div>
          <div className="flex items-center justify-between mt-4 pt-3 border-t border-border/50">
            <StarRating rating={rating} />
            {product.category_name && (
              <Badge variant="secondary" size="sm">
                {product.category_name}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
}

export const MarketplaceHomePage = observer(() => {
  const store = useProduct();
  const { t } = useI18n();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [categories, setCategories] = useState<any[]>([]);
  const [featured, setFeatured] = useState<any[]>([]);
  const [newest, setNewest] = useState<any[]>([]);

  useEffect(() => {
    // Load categories
    store.ListProductCategories().then((res) => {
      const items = res?.categories?.items;
      setCategories(Array.isArray(items) ? items : []);
    }).catch(() => {});

    // Load featured (active, public) products
    productApi.ListProducts({
      pagination: { cursor: '', limit: 8, direction: 'forward' },
      status: 'PRODUCT_STATUS_ACTIVE',
    }).then((res) => {
      const items = res?.products?.items;
      setFeatured(Array.isArray(items) ? items : []);
    }).catch(() => {
      setFeatured(store.items ?? []);
    });

    // Load newest products
    productApi.ListProducts({
      pagination: { cursor: '', limit: 8, direction: 'forward' },
      sort: [{ field: 'created_at', order: 'desc' }],
    }).then((res) => {
      const items = res?.products?.items;
      setNewest(Array.isArray(items) ? items : []);
    }).catch(() => {});
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/marketplace/search?q=${encodeURIComponent(searchQuery.trim())}`);
    }
  };

  return (
    <div className="space-y-10">
      {/* Hero Section */}
      <section className="rounded-xl border border-border bg-card px-6 py-8">
        <div className="max-w-2xl mx-auto text-center">
          <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2 tracking-tight">
            {t('marketplace.title')}
          </h1>
          <p className="text-sm text-muted-foreground mb-5">
            {t('marketplace.subtitle')}
          </p>
          <form onSubmit={handleSearch} className="max-w-lg mx-auto flex gap-2">
            <div className="flex-1 relative">
              <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <circle cx="11" cy="11" r="8" />
                <path d="M21 21l-4.35-4.35" />
              </svg>
              <Input
                placeholder={t('marketplace.search')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                fullWidth={false}
                className="w-full pl-9 h-10"
              />
            </div>
            <Button type="submit" className="h-10 px-5">
              {t('marketplace.searchButton')}
            </Button>
          </form>
          <div className="flex items-center justify-center gap-4 mt-4 text-xs text-muted-foreground">
            <span>{featured.length}+ {t('marketplace.products')}</span>
            <span className="w-1 h-1 rounded-full bg-border" />
            <span>{categories.length} {t('marketplace.categories').toLowerCase()}</span>
          </div>
        </div>
      </section>

      {store.error ? <ErrorAlert error={store.error} dismissible onDismiss={() => store.clearError()} /> : null}

      {/* Categories */}
      {categories.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-foreground">
              {t('marketplace.categories')}
            </h2>
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((cat: any) => (
              <Link
                key={cat.entity?.id || cat.slug}
                to={`/marketplace/categories/${cat.slug}`}
                className="group inline-flex items-center gap-2.5 px-4 py-2.5 rounded-xl border border-border bg-card hover:border-primary/30 hover:shadow-sm transition-all"
              >
                {cat.icon_url ? (
                  <img src={cat.icon_url} alt={cat.name} className="w-5 h-5 object-contain" />
                ) : (
                  <div
                    className="w-5 h-5 rounded flex items-center justify-center text-white text-[10px] font-bold"
                    style={{ backgroundColor: cat.color || '#6366f1' }}
                  >
                    {(cat.name ?? '?')[0]?.toUpperCase()}
                  </div>
                )}
                <span className="text-sm font-medium text-foreground group-hover:text-primary transition-colors">
                  {cat.name}
                </span>
                {cat.product_count != null && (
                  <span className="text-xs text-muted-foreground tabular-nums">
                    {cat.product_count}
                  </span>
                )}
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* Featured Products */}
      {featured.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-foreground">
              {t('marketplace.featured')}
            </h2>
            <Link to="/marketplace/search" className="text-sm text-primary hover:text-primary/80 font-medium transition-colors">
              {t('marketplace.viewAll')} &rarr;
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {featured.map((product: any) => (
              <ProductCard
                key={product.entity?.id || product.slug}
                product={product}
                marketplace={product._marketplace}
              />
            ))}
          </div>
        </section>
      )}

      {/* New Products */}
      {newest.length > 0 && (
        <section>
          <div className="flex items-center justify-between mb-5">
            <h2 className="text-xl font-semibold text-foreground">
              {t('marketplace.newProducts')}
            </h2>
            <Link
              to="/marketplace/search?sort=newest"
              className="text-sm text-primary hover:text-primary/80 font-medium transition-colors"
            >
              {t('marketplace.viewAll')} &rarr;
            </Link>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {newest.map((product: any) => (
              <ProductCard
                key={product.entity?.id || product.slug}
                product={product}
                marketplace={product._marketplace}
              />
            ))}
          </div>
        </section>
      )}

      {/* Loading state */}
      {store.isLoading && (
        <div className="flex items-center justify-center h-32">
          <div className="text-muted-foreground">{t('common.loading')}</div>
        </div>
      )}
    </div>
  );
});

export default MarketplaceHomePage;
